import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, ExternalLink } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import { getAllFeatureConfigs, getRawConfig } from '../../config/featureDisplayConfig';

/**
 * FeatureAdminPage - Admin dashboard to view/manage all feature display configs.
 * 
 * Shows each feature's:
 *   - Metadata (name, icon, paths)
 *   - Grid columns (visible/hidden)
 *   - Form fields
 */
const FeatureAdminPage = ({ userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const features = getAllFeatureConfigs();
  const [expandedFeature, setExpandedFeature] = useState(null);
  const [enabledState, setEnabledState] = useState(() => {
    const state = {};
    features.forEach(f => { state[f.key] = f.enabled !== false; });
    return state;
  });

  const toggleExpand = (key) => {
    setExpandedFeature(expandedFeature === key ? null : key);
  };

  const handleToggleEnabled = async (featureKey) => {
    const newEnabled = !enabledState[featureKey];
    setEnabledState(prev => ({ ...prev, [featureKey]: newEnabled }));
    try {
      const rawConfig = getRawConfig();
      if (rawConfig[featureKey]) {
        rawConfig[featureKey].enabled = newEnabled;
        // Save via admin server
        await fetch('http://localhost:4000/api/save-config', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rawConfig),
        });
      }
    } catch (err) {
      console.warn('Failed to save config:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Quản lý tính năng"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-base font-semibold text-gray-900 flex-1">Quản lý tính năng</h1>
          <span className="text-xs text-gray-400">{features.length} tính năng</span>
        </div>

        {/* Info banner */}
        <div className="mx-3 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>Hướng dẫn:</strong> Đây là trang tổng quan các tính năng. Để thay đổi cấu hình hiển thị (ẩn/hiện cột, field), 
            chỉnh sửa file <code className="bg-blue-100 px-1 rounded">src/config/featureDisplayConfig.js</code>
          </p>
        </div>

        {/* Feature cards */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 pb-20">
          {features.map((feature) => {
            const isEnabled = enabledState[feature.key] !== false;
            return (
            <div key={feature.key} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-opacity ${isEnabled ? 'border-gray-200' : 'border-gray-300 opacity-60'}`}>
              {/* Feature header */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(feature.key)}
              >
                {/* Toggle button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleToggleEnabled(feature.key); }}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isEnabled ? 'text-white' : 'bg-gray-200 text-gray-500'}`}
                  style={isEnabled ? { backgroundColor: feature.color || '#1E5A35' } : {}}
                  title={isEnabled ? 'Ấn để ẩn' : 'Ấn để hiện'}
                >
                  <span className="material-symbols-rounded text-xl">{feature.icon}</span>
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`text-sm font-semibold ${isEnabled ? 'text-gray-900' : 'text-gray-400 line-through'}`}>{feature.name}</h3>
                    {!isEnabled && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">Đã ẩn</span>}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{feature.path}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(feature.path); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-[#1E5A35] hover:bg-green-50 transition-colors"
                  title="Mở tính năng"
                >
                  <ExternalLink size={16} />
                </button>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${expandedFeature === feature.key ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Expanded detail */}
              {expandedFeature === feature.key && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-4">
                  {/* Grid columns */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      📊 Cột grid hiển thị ({feature.listConfig.displayMode})
                    </h4>
                    <div className="space-y-1">
                      {feature.listConfig.columns.map((col) => (
                        <div key={col.key} className="flex items-center gap-2 py-1">
                          {col.visible !== false ? (
                            <Eye size={14} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <EyeOff size={14} className="text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`text-xs ${col.visible !== false ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                            {col.label}
                          </span>
                          <span className="text-[10px] text-gray-400 ml-auto font-mono">{col.key}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary fields */}
                  {feature.listConfig.summaryFields.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                        📈 Summary fields
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {feature.listConfig.summaryFields.map((sf) => (
                          <span key={sf.key} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            {sf.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Form fields */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      📝 Form fields ({feature.formConfig.fields.length})
                    </h4>
                    <div className="space-y-1">
                      {feature.formConfig.fields.map((field) => (
                        <div key={field.key} className="flex items-center gap-2 py-1">
                          <span className="text-[10px] font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded w-16 text-center flex-shrink-0">
                            {field.type}
                          </span>
                          <span className="text-xs text-gray-800 flex-1">{field.label}</span>
                          {field.required && (
                            <span className="text-[10px] text-red-500 font-medium">bắt buộc</span>
                          )}
                          {field.readonly && (
                            <span className="text-[10px] text-gray-400 font-medium">readonly</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Paths */}
                  <div className="border-t border-gray-100 pt-3">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                      🔗 Đường dẫn
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <p className="text-gray-500">Danh sách</p>
                        <code className="text-[10px] text-blue-600">{feature.path}</code>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Tạo mới</p>
                        <code className="text-[10px] text-blue-600">{feature.createPath}</code>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Chỉnh sửa</p>
                        <code className="text-[10px] text-blue-600">{feature.editPath}</code>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeatureAdminPage;
